import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useMercenary } from '../../../contexts/MercenaryContext';
import './History.css';
import { formatDateTime } from '../../../utils/dateFormat';

interface HistoryRecord {
    id: number;
    itemName: string;
    price: number;
    quantity: number;
    status: 'expired' | 'canceled' | 'fulfilled';
    createdAt: string;
    updatedAt: string;
    type: 'buy' | 'sell';
    role: 'buyer' | 'seller';
}

const STATUS_BADGE: Record<string, { cls: string }> = {
    fulfilled: { cls: 'hist-fulfilled' },
    expired: { cls: 'hist-expired' },
    canceled: { cls: 'hist-canceled' },
};

const History: React.FC = () => {
    const { mercenary } = useMercenary();
    const [history, setHistory] = useState<HistoryRecord[]>([]);
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const limit = window.innerHeight < 1000 ? 14 : 20;

    useEffect(() => {
        if (mercenary?.id) {
            axios.get(`/api/auction/history/${mercenary.id}?page=${page}&limit=${limit}`)
                .then(response => {
                    const { history: orderHistory, totalRecords } = response.data;
                    setHistory(orderHistory.map((order: any) => ({
                        id: order.id,
                        itemName: order.Item.name,
                        price: order.price,
                        quantity: order.quantity,
                        status: order.status,
                        updatedAt: order.updatedAt,
                        type: order.order_type,
                        role: order.role,
                    })));
                    setTotalRecords(totalRecords);
                })
                .catch(error => console.error('Error fetching history:', error));
        }
    }, [mercenary, page]);

    const totalPages = Math.ceil(totalRecords / limit);

    const formatStatus = (role: 'buyer' | 'seller', status: string) => {
        const label = role === 'buyer' ? 'Purchase' : 'Sale';
        return `${label}, ${status.charAt(0).toUpperCase() + status.slice(1)}`;
    };

    return (
        <div className="hist-container">
            <h2>Auction History</h2>

            <div className="hist-scroll">
                {history.length > 0 ? (
                    <table className="hist-table">
                        <thead>
                            <tr>
                                <th>Item</th>
                                <th>Qty</th>
                                <th>Price</th>
                                <th>Status</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.map(record => {
                                const badge = STATUS_BADGE[record.status] ?? { cls: '' };
                                return (
                                    <tr key={record.id} className={badge.cls}>
                                        <td className="hist-cell-name">{record.itemName}</td>
                                        <td className="hist-cell-qty">{record.quantity}</td>
                                        <td className="hist-cell-price">{record.price} silver</td>
                                        <td className="hist-cell-status">
                                            <span className={`hist-badge ${badge.cls}`}>
                                                {formatStatus(record.role, record.status)}
                                            </span>
                                        </td>
                                        <td className="hist-cell-date">{formatDateTime(record.updatedAt)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    <p className="hist-empty">No auction history yet</p>
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="hist-pagination">
                    <button disabled={page <= 1} onClick={() => setPage(1)}>«</button>
                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
                    <span className="hist-page-info">Page {page} of {totalPages}</span>
                    <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
                    <button disabled={page >= totalPages} onClick={() => setPage(totalPages)}>»</button>
                </div>
            )}
        </div>
    );
};

export default History;
